import { useEffect, useMemo, useState } from "react";
import type { LeadTalkTimeLogRow } from "@/api/leads";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { leadsApi, type LeadTalkTimeLogsResponse, type LeadTalkTimeReport } from "@/api/leads";
import { hospitalAdminApi } from "@/api/hospitalAdmin";
import { useAuth } from "@/contexts/AuthContext";
import { canDeleteLeadCallRecording } from "@/utils/crmAccess";
import { getApiErrorMessage } from "@/utils/apiErrorMessage";
import { LeadActivityAudioPlayer } from "@/components/crm/LeadActivityAudioPlayer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { formatLeadSourceLabel } from "@/lib/leadSources";
import { ArrowDown, ArrowUp, ArrowUpDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { reportHospitalStaffParams } from "@/utils/reportHospitalStaffParams";

type Props = {
  from: string;
  to: string;
  onFromChange?: (v: string) => void;
  onToChange?: (v: string) => void;
  hospitalId?: string;
};

const LOG_PAGE_SIZE = 100;

type TalkTimeLogSort = "callAt_desc" | "callAt_asc" | "duration_desc" | "duration_asc";

function fmtTalkSeconds(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s <= 0) return "0s";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function fmtTalkMinutesForChart(seconds: number): number {
  return Math.round((seconds / 60) * 10) / 10;
}

function fmtCallDateTimeIst(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

function SummaryCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function LeadTalkTimeTab({ from, to, onFromChange, onToChange, hospitalId }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canDeleteRecording = canDeleteLeadCallRecording(user);
  const selfOnly = user?.role === "SALES_PERSON";
  const canPickStaff =
    !selfOnly &&
    (user?.role === "HOSPITAL_ADMIN" ||
      user?.role === "SUPER_ADMIN" ||
      user?.role === "SALES_MANAGER");

  const [staffId, setStaffId] = useState("");
  const [source, setSource] = useState("");
  const [campaign, setCampaign] = useState("");
  const [logOffset, setLogOffset] = useState(0);
  const [allLogs, setAllLogs] = useState<LeadTalkTimeLogRow[]>([]);
  const [logSort, setLogSort] = useState<TalkTimeLogSort>("callAt_desc");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const reportParams = useMemo(() => {
    const p: Record<string, string> = { from, to };
    if (hospitalId) p.hospitalId = hospitalId;
    if (staffId) p.staffId = staffId;
    return p;
  }, [from, to, hospitalId, staffId]);

  const logParams = useMemo(() => {
    const p: Record<string, string | number> = {
      from,
      to,
      limit: LOG_PAGE_SIZE,
      offset: logOffset,
    };
    if (hospitalId) p.hospitalId = hospitalId;
    if (staffId) p.staffId = staffId;
    if (source) p.source = source;
    if (campaign) p.campaignName = campaign;
    p.sort = logSort;
    return p;
  }, [from, to, hospitalId, staffId, source, campaign, logOffset, logSort]);

  const filterOptionsQ = useQuery({
    queryKey: ["lead-filter-options-talk-time", hospitalId ?? "all"],
    queryFn: async () => leadsApi.getLeadFilterOptions(hospitalId ? { hospitalId } : undefined),
  });

  const staffQ = useQuery({
    queryKey: ["hospital-staff-talk-time", hospitalId ?? user?.hospitalId],
    queryFn: async () => hospitalAdminApi.getStaff(reportHospitalStaffParams(hospitalId)),
    enabled: canPickStaff,
  });

  const salesStaff = useMemo(() => {
    const roles = new Set(["SALES_PERSON", "SALES_MANAGER"]);
    return (staffQ.data ?? [])
      .filter((s) => roles.has(String(s.role)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [staffQ.data]);

  const talkQuery = useQuery({
    queryKey: ["lead-talk-time-report", reportParams],
    queryFn: async () =>
      (await leadsApi.getTalkTimeReport(reportParams)).data.data as LeadTalkTimeReport,
  });

  const logsQuery = useQuery({
    queryKey: ["lead-talk-time-logs", logParams],
    queryFn: async () => (await leadsApi.getTalkTimeLogs(logParams)).data.data as LeadTalkTimeLogsResponse,
    enabled: !!from && !!to,
  });

  useEffect(() => {
    setLogOffset(0);
    setAllLogs([]);
  }, [from, to, hospitalId, staffId, source, campaign, logSort]);

  const deleteRecordingMut = useMutation({
    mutationFn: (recordingId: string) => leadsApi.deleteCallRecording(recordingId),
    onSuccess: (_res, recordingId) => {
      setDeleteConfirmId(null);
      setAllLogs((prev) =>
        prev.map((r) =>
          r.recordingId === recordingId
            ? { ...r, hasRecording: false, recordingId: null }
            : r
        )
      );
      void queryClient.invalidateQueries({ queryKey: ["lead-talk-time-logs"] });
      void queryClient.invalidateQueries({ queryKey: ["lead-talk-time-report"] });
      toast.success("Recording deleted");
    },
    onError: (e: unknown) => {
      toast.error(getApiErrorMessage(e, "Could not delete recording"));
    },
  });

  const toggleDurationSort = () => {
    setLogSort((prev) => {
      if (prev === "duration_desc") return "duration_asc";
      return "duration_desc";
    });
  };

  useEffect(() => {
    const rows = logsQuery.data?.rows;
    if (!rows) return;
    if (logOffset === 0) {
      setAllLogs(rows);
      return;
    }
    setAllLogs((prev) => {
      const seen = new Set(prev.map((r) => r.id));
      return [...prev, ...rows.filter((r) => !seen.has(r.id))];
    });
  }, [logsQuery.dataUpdatedAt, logOffset, logsQuery.data?.rows]);

  const logRows = allLogs;

  const data = talkQuery.data;
  const summary = data?.summary;

  const personChartRows = useMemo(() => {
    return (data?.bySalesPerson ?? []).map((r) => ({
      name: r.name.length > 18 ? `${r.name.slice(0, 16)}…` : r.name,
      fullName: r.name,
      exotelMin: fmtTalkMinutesForChart(r.exotelSeconds),
      appMin: fmtTalkMinutesForChart(r.appSeconds),
      totalMin: fmtTalkMinutesForChart(r.totalSeconds),
    }));
  }, [data?.bySalesPerson]);

  const dayChartRows = useMemo(() => {
    return (data?.byDay ?? []).map((d) => ({
      date: d.date.slice(5),
      fullDate: d.date,
      exotelMin: fmtTalkMinutesForChart(d.exotelSeconds),
      appMin: fmtTalkMinutesForChart(d.appSeconds),
      totalMin: fmtTalkMinutesForChart(d.totalSeconds),
    }));
  }, [data?.byDay]);

  const sources: string[] = filterOptionsQ.data?.sources ?? [];
  const campaigns: string[] = filterOptionsQ.data?.campaignNames ?? [];

  const handleLoadMoreLogs = () => {
    if (!logsQuery.data?.hasMore || logsQuery.isFetching) return;
    setLogOffset((o) => o + LOG_PAGE_SIZE);
  };

  if (talkQuery.isLoading) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Loading talk time…</p>;
  }
  if (talkQuery.isError) {
    return (
      <p className="text-sm text-destructive py-8 text-center">Could not load talk time report.</p>
    );
  }

  const singleDay = from === to;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {selfOnly
          ? "Your talk time from Exotel and sales-app outbound recordings (IST calendar days)."
          : "Combined talk time from Exotel (inbound/agent) and sales-app outbound recordings in lead history."}{" "}
        Times use {data?.timezone ?? "Asia/Kolkata"}.
      </p>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Label className="text-xs text-muted-foreground">From</Label>
            {onFromChange ? (
              <Input
                type="date"
                value={from}
                onChange={(e) => onFromChange(e.target.value)}
              />
            ) : (
              <Input type="date" value={from} readOnly className="bg-muted/40" />
            )}
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">To</Label>
            {onToChange ? (
              <Input
                type="date"
                value={to}
                onChange={(e) => onToChange(e.target.value)}
              />
            ) : (
              <Input type="date" value={to} readOnly className="bg-muted/40" />
            )}
          </div>
          {canPickStaff ? (
            <div>
              <Label className="text-xs text-muted-foreground">Sales person</Label>
              <Select
                value={staffId || "__all__"}
                onValueChange={(v) => setStaffId(v === "__all__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All sales people</SelectItem>
                  {salesStaff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div>
            <Label className="text-xs text-muted-foreground">Lead source</Label>
            <Select
              value={source || "__all__"}
              onValueChange={(v) => setSource(v === "__all__" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All sources</SelectItem>
                {sources.map((s) => (
                  <SelectItem key={s} value={s}>
                    {formatLeadSourceLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Campaign</Label>
            <Select
              value={campaign || "__all__"}
              onValueChange={(v) => setCampaign(v === "__all__" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All campaigns" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All campaigns</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label="Total talk time"
          value={fmtTalkSeconds(summary?.totalSeconds)}
          hint={`${summary?.totalCalls ?? 0} calls`}
        />
        <SummaryCard
          label="Exotel"
          value={fmtTalkSeconds(summary?.exotelSeconds)}
          hint={`${summary?.exotelCalls ?? 0} calls`}
        />
        <SummaryCard
          label="Sales app"
          value={fmtTalkSeconds(summary?.appSeconds)}
          hint={`${summary?.appCalls ?? 0} calls`}
        />
        <SummaryCard
          label="Period"
          value={singleDay ? from : `${from} → ${to}`}
          hint={singleDay ? "Single day" : "Inclusive IST dates"}
        />
      </div>

      {personChartRows.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {selfOnly
                ? singleDay
                  ? "Your talk time (today)"
                  : "Your talk time"
                : singleDay
                  ? "Talk time by sales person (today)"
                  : "Talk time by sales person"}
            </CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={personChartRows} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" angle={-35} textAnchor="end" interval={0} height={56} fontSize={11} />
                <YAxis unit=" min" fontSize={11} />
                <Tooltip
                  formatter={(v: number, key: string) => {
                    const label =
                      key === "exotelMin" ? "Exotel" : key === "appMin" ? "Sales app" : "Total";
                    return [`${v} min`, label];
                  }}
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.fullName ? String(payload[0].payload.fullName) : ""
                  }
                />
                <Legend />
                <Bar dataKey="exotelMin" name="Exotel (min)" stackId="a" fill="#2563eb" />
                <Bar dataKey="appMin" name="Sales app (min)" stackId="a" fill="#16a34a" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      ) : null}

      {!singleDay && dayChartRows.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Daily talk time</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dayChartRows} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" fontSize={11} />
                <YAxis unit=" min" fontSize={11} />
                <Tooltip
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.fullDate ? String(payload[0].payload.fullDate) : ""
                  }
                />
                <Legend />
                <Bar dataKey="exotelMin" name="Exotel (min)" stackId="b" fill="#2563eb" />
                <Bar dataKey="appMin" name="Sales app (min)" stackId="b" fill="#16a34a" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {selfOnly ? "Your breakdown" : "Sales person breakdown"}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Sales person</th>
                <th className="py-2 pr-4 font-medium text-right">Exotel calls</th>
                <th className="py-2 pr-4 font-medium text-right">Exotel time</th>
                <th className="py-2 pr-4 font-medium text-right">App calls</th>
                <th className="py-2 pr-4 font-medium text-right">App time</th>
                <th className="py-2 pr-4 font-medium text-right">Total calls</th>
                <th className="py-2 font-medium text-right">Total time</th>
              </tr>
            </thead>
            <tbody>
              {(data?.bySalesPerson ?? []).map((r) => (
                <tr key={r.staffId ?? r.name} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-medium text-slate-900">{r.name}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{r.exotelCalls}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{fmtTalkSeconds(r.exotelSeconds)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{r.appCalls}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{fmtTalkSeconds(r.appSeconds)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{r.totalCalls}</td>
                  <td className="py-2 text-right tabular-nums font-medium">
                    {fmtTalkSeconds(r.totalSeconds)}
                  </td>
                </tr>
              ))}
              {!data?.bySalesPerson?.length ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-muted-foreground">
                    No recorded talk time in this period.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Talk time logs</CardTitle>
          <p className="text-sm text-muted-foreground">
            One row per connected call. Sales-app recordings use full-length THMS storage; Exotel uses
            the recording URL from the call webhook when available.
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {logsQuery.isLoading && logOffset === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading call logs…</p>
          ) : logsQuery.isError ? (
            <p className="py-6 text-center text-sm text-destructive">Could not load talk time logs.</p>
          ) : (
            <>
              <table className="w-full min-w-[960px] text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Patient</th>
                    <th className="py-2 pr-3 font-medium">Phone</th>
                    <th className="py-2 pr-3 font-medium whitespace-nowrap">Call date & time</th>
                    <th className="py-2 pr-3 font-medium">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 hover:text-slate-900"
                        onClick={toggleDurationSort}
                        title="Sort by call duration"
                      >
                        Duration
                        {logSort === "duration_desc" ? (
                          <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                        ) : logSort === "duration_asc" ? (
                          <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <ArrowUpDown className="h-3.5 w-3.5 opacity-50" aria-hidden />
                        )}
                      </button>
                    </th>
                    <th className="py-2 pr-3 font-medium">Sales person</th>
                    <th className="py-2 pr-3 font-medium">Source</th>
                    <th className="py-2 pr-3 font-medium">Channel</th>
                    <th className="py-2 font-medium min-w-[260px]">Recording</th>
                  </tr>
                </thead>
                <tbody>
                  {logRows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0 align-top">
                      <td className="py-2 pr-3 font-medium text-slate-900">{row.patientName}</td>
                      <td className="py-2 pr-3 tabular-nums text-slate-700">{row.phone}</td>
                      <td className="py-2 pr-3 whitespace-nowrap text-slate-700">
                        {fmtCallDateTimeIst(row.callAt)}
                      </td>
                      <td className="py-2 pr-3 tabular-nums whitespace-nowrap">
                        {fmtTalkSeconds(row.durationSec)}
                      </td>
                      <td className="py-2 pr-3 text-slate-700">{row.salesPersonName}</td>
                      <td className="py-2 pr-3 text-slate-700">
                        {row.leadSource ? formatLeadSourceLabel(row.leadSource) : "—"}
                      </td>
                      <td className="py-2 pr-3 text-slate-700">
                        {row.channel === "EXOTEL" ? "Exotel" : "Sales app"}
                      </td>
                      <td className="py-2 pr-1">
                        {row.hasRecording ? (
                          <div className="flex flex-col gap-2 max-w-[320px]">
                            <LeadActivityAudioPlayer
                              className="mt-0 max-w-[280px]"
                              showControls
                              recordingId={
                                row.channel === "SALES_APP" ? row.recordingId ?? undefined : undefined
                              }
                              activityId={
                                row.channel === "SALES_APP" ? row.activityId ?? undefined : undefined
                              }
                              exotelActivityId={
                                row.channel === "EXOTEL" ? row.activityId ?? undefined : undefined
                              }
                              label="Play recording"
                            />
                            {canDeleteRecording &&
                            row.channel === "SALES_APP" &&
                            row.recordingId ? (
                              deleteConfirmId === row.recordingId ? (
                                <div className="flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5">
                                  <span className="text-xs text-red-900">Delete permanently?</span>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="destructive"
                                    className="h-7 text-xs"
                                    disabled={deleteRecordingMut.isPending}
                                    onClick={() =>
                                      deleteRecordingMut.mutate(row.recordingId!)
                                    }
                                  >
                                    {deleteRecordingMut.isPending ? "Deleting…" : "Delete"}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs"
                                    disabled={deleteRecordingMut.isPending}
                                    onClick={() => setDeleteConfirmId(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-fit gap-1 text-xs text-red-800 border-red-200 hover:bg-red-50"
                                  onClick={() => setDeleteConfirmId(row.recordingId!)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                                  Delete recording
                                </Button>
                              )
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!logRows.length ? (
                    <tr>
                      <td colSpan={8} className="py-6 text-center text-muted-foreground">
                        No calls match these filters.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
              {logsQuery.data?.hasMore ? (
                <div className="mt-4 flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={logsQuery.isFetching}
                    onClick={handleLoadMoreLogs}
                  >
                    {logsQuery.isFetching ? "Loading…" : `Load more (${logRows.length} of ${logsQuery.data?.total ?? "—"})`}
                  </Button>
                </div>
              ) : logsQuery.data?.total ? (
                <p className="mt-3 text-center text-xs text-muted-foreground">
                  Showing {logRows.length} of {logsQuery.data.total} calls
                </p>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
